import { useEffect, useRef } from "react";
import { SplashScreen } from "@capacitor/splash-screen";

const STORAGE_KEY = "cc_intro_shown";
const MAX_MS = 20000; // hard safety cap so the user is never stuck

// Hide the native OS splash. No-op on web. Called once the video is painting
// (or on any early exit) so there is never a white flash between splash/video/app.
function hideNativeSplash() {
  try {
    SplashScreen.hide();
  } catch (_) {}
}

// Play only once per app cold-start. sessionStorage survives in-app
// navigation and background/foreground within the same session, but is
// cleared when the OS fully terminates the app -> replays on next launch.
export function shouldPlayIntro() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== "1";
  } catch (_) {
    return true;
  }
}

export default function IntroVideo({ onFinish }) {
  const videoRef = useRef(null);
  const doneRef = useRef(false);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    hideNativeSplash();
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {}
    onFinish && onFinish();
  };

  useEffect(() => {
    const v = videoRef.current;
    const timer = setTimeout(finish, MAX_MS);

    const play = async () => {
      if (!v) return;
      try {
        await v.play();
      } catch (err) {
        // Autoplay with sound blocked -> retry muted to guarantee playback.
        try {
          v.muted = true;
          await v.play();
        } catch (err2) {
          console.error("[IntroVideo] playback failed", err2);
          finish();
        }
      }
    };
    play();

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="intro-video-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      <video
        ref={videoRef}
        data-testid="intro-video"
        src="/intro.mp4"
        playsInline
        preload="auto"
        className="max-h-full max-w-full"
        style={{ objectFit: "contain" }}
        onPlaying={hideNativeSplash}
        onEnded={finish}
        onError={(e) => {
          console.error("[IntroVideo] failed to load/play", e);
          finish();
        }}
      />
    </div>
  );
}
